import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { compressImages } from '@/lib/imageCompression';

export const useEventPhotoUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const uploadPhotos = async (files: File[], userId: string): Promise<string[]> => {
    if (files.length === 0) return [];

    setIsCompressing(true);
    setUploadProgress(0);
    const uploadedUrls: string[] = [];

    try {
      // Compress images first
      const compressedFiles = await compressImages(files, {
        maxWidth: 2048,
        maxHeight: 2048,
        quality: 0.8,
        preserveExif: true,
      });
      
      setIsCompressing(false);
      setIsUploading(true);

      for (let i = 0; i < compressedFiles.length; i++) {
        const file = compressedFiles[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { data, error } = await supabase.storage
          .from('event-photos')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (error) {
          console.error('Error uploading photo:', error);
          throw error;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('event-photos')
          .getPublicUrl(data.path);

        uploadedUrls.push(publicUrl);
        setUploadProgress(((i + 1) / compressedFiles.length) * 100);
      }

      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload some photos');
      return uploadedUrls; // Return successfully uploaded photos
    } finally {
      setIsUploading(false);
      setIsCompressing(false);
      setUploadProgress(0);
    }
  };

  const deletePhoto = async (photoUrl: string): Promise<boolean> => {
    try {
      // Extract path from URL
      const path = photoUrl.split('/event-photos/')[1];
      
      if (!path) {
        console.error('Invalid photo URL:', photoUrl);
        return false;
      }

      const { error } = await supabase.storage
        .from('event-photos')
        .remove([path]);

      if (error) {
        console.error('Error deleting photo:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
      return false;
    }
  };

  return {
    uploadPhotos,
    deletePhoto,
    isUploading,
    isCompressing,
    uploadProgress,
  };
};
